import { Column, HeaderContext } from '@tanstack/react-table';
import { TEST_IDS } from '@va/constants';
import { ArrowsIcon, FilterIcon, IconProps, SortAndFilterIcon } from '@va/icons';
import { fontWeights, GroupingOption, HeaderCellFiltersDropdown, Paragraph } from '@va/ui/design-system';
import { TooltipWrapper, useTooltipContext } from '@va/ui/tooltips';
import { useCheckOverflow } from '@va/util/hooks';
import classNames from 'classnames';
import { ReactNode, useCallback, useMemo, useRef } from 'react';
import { ColumnOptionsButton } from './ColumnOptionsButton';
import { useColumnSizingContext } from './column-sizing/ColumnSizingContext';

type HeaderCellV8Props<TData, TValue> = {
  context: HeaderContext<TData, TValue>;
  className?: string;
  text?: string;
  icon?: ReactNode;
  tooltip?: string;
  iconTooltip?: ReactNode;
  groupingOptions?: GroupingOption[];
  size?: 'small' | 'medium';
  additionalInfo?: ReactNode;
  align?: 'left' | 'center' | 'right';
};

export const HeaderCellV8 = <TData, TValue>({
  context,
  icon,
  className,
  text,
  tooltip,
  iconTooltip,
  groupingOptions,
  size = 'medium',
  additionalInfo,
  align = 'center',
}: HeaderCellV8Props<TData, TValue>) => {
  const { isProvided: isColumnSizingEnabled, storedColumnSizing, onColumnSizingChange } = useColumnSizingContext();

  const canSort = context.column.getCanSort();
  const isSorted = context.column.getIsSorted();
  const isGrouped = context.column.getIsGrouped();
  const canGroup = context.column.getCanGroup();
  const group = context.table.getState().grouping;

  if (text && context.column.setLabel) {
    // Only display string values. Tolgee can sometimes return an object (e.g. for <br> tags), which causes issues in the header.
    const parsedText = Array.isArray(text) ? text.filter((t) => typeof t === 'string').join(' ') : text;
    context.column.setLabel(parsedText);
  }

  const defaultSorting = useMemo(() => {
    const sorting = context.table.options.meta?.defaultSorting?.find((s) => s.id === context.column.id);

    if (!sorting) return null;

    return sorting.desc ? 'desc' : 'asc';
  }, [context.column.id, context.table.options.meta?.defaultSorting]);

  const handleSorting = useCallback(() => {
    const nextSortingOrder = context.column.getNextSortingOrder();

    if (defaultSorting && isSorted && !nextSortingOrder) {
      context.column.toggleSorting(isSorted === 'asc' ? true : false);
      return;
    }

    context.column.toggleSorting();
  }, [context.column, defaultSorting, isSorted]);

  const shouldHighlightHeader = useMemo(() => {
    if (!canGroup) return false;
    if (isSorted) return true;
    if (isGrouped) return true;
    return !!groupingOptions?.find((item) => group.includes(item.value));
  }, [canGroup, group, groupingOptions, isGrouped, isSorted]);

  const arrowIcon = useMemo(() => {
    if (!isSorted) return <ArrowsIcon />;
    return (
      <ChevronIcon
        className={classNames({ 'rotate-180': isSorted === 'asc' })}
        color={isSorted ? 'var(--color-primary)' : undefined}
      />
    );
  }, [isSorted]);

  const textRef = useRef<HTMLDivElement>(null);
  const isTruncated = useCheckOverflow(textRef);

  const headerTooltipContent = useMemo(() => {
    if (tooltip) {
      return (
        <div>
          <Paragraph weight={fontWeights.semibold} className='mb-1'>
            {text}
          </Paragraph>
          <Paragraph>{tooltip}</Paragraph>
        </div>
      );
    }

    return <Paragraph>{text}</Paragraph>;
  }, [text, tooltip]);

  const isFirstColumn = context.table.getAllLeafColumns()[0]?.id === context.column.id;
  const canHide = !isFirstColumn;
  const hasCustomWidth = storedColumnSizing[context.column.id] !== undefined;
  const showOptionsButton = canHide || hasCustomWidth;

  return (
    <div
      className={classNames(
        'header-cell-content w-full h-full font-medium leading-normal text-xs text-gray-faded flex items-center divide-x-4 divide-transparent flex-nowrap',
        {
          'bg-gray-mercury': shouldHighlightHeader,
          'bg-white-snow ': !shouldHighlightHeader,
          'pl-2 pr-6': size === 'medium' && !isColumnSizingEnabled,
          'pl-2': size === 'medium' && isColumnSizingEnabled,
          'pl-3 pr-4': size === 'small' && !isColumnSizingEnabled,
          'pl-3': size === 'small' && isColumnSizingEnabled,
        },
        className,
      )}
    >
      <div
        className={classNames('flex items-center flex-1 min-w-0 divide-x-4 divide-transparent', {
          'py-4': size === 'medium',
          'py-2': size === 'small',
          'justify-start': align === 'left',
          'justify-center': align === 'center',
          'justify-end': align === 'right',
        })}
      >
        {icon && (
          <TooltipWrapper content={iconTooltip} disabled={!iconTooltip} interactive useDefaultStyle={false}>
            <div className='flex items-center justify-center'>{icon}</div>
          </TooltipWrapper>
        )}
        <TooltipWrapper
          trigger={'click'}
          disabled={!tooltip && !isTruncated}
          content={headerTooltipContent}
          interactive
        >
          <div
            ref={textRef}
            className={classNames({
              'cursor-default truncate': typeof text === 'string',
              'text-left': align === 'left',
              'text-center': align === 'center',
              'text-right': align === 'right',
              'underline-dashed !border-b-gray-silver': tooltip,
              'text-gray-devilSolid': !isSorted,
              'text-primary font-semibold': isSorted,
            })}
          >
            {text}
          </div>
        </TooltipWrapper>
        {additionalInfo}

        <TooltipWrapper
          trigger='click'
          arrow={false}
          disabled={!canGroup}
          placement='bottom'
          useDefaultStyle={false}
          tooltipClassNames='border-2 border-gray-concrete rounded-24 shadow-overlay'
          content={<TooltipContent ctx={context} groupingOptions={groupingOptions} />}
        >
          <div
            onClick={() => {
              if (!canSort || canGroup) return;
              handleSorting();
            }}
          >
            {canSort && !canGroup && (
              <div
                className='cursor-pointer flex items-center justify-center'
                data-testid={TEST_IDS.generic.table.sortHeaderBtn}
              >
                {arrowIcon}
              </div>
            )}
            {!canSort && canGroup && (
              <div
                className='cursor-pointer flex items-center justify-center ml-2'
                data-testid={TEST_IDS.generic.table.groupHeaderBtn}
              >
                <FilterIcon color='#969696' className='h-18px w-18px' />
              </div>
            )}
            {canSort && canGroup && (
              <div
                className='cursor-pointer flex items-center justify-center ml-2'
                data-testid={TEST_IDS.generic.table.sortAndGroupHeaderBtn}
              >
                <SortAndFilterIcon color='#969696' className='h-18px w-18px' />
              </div>
            )}
          </div>
        </TooltipWrapper>
      </div>

      {isColumnSizingEnabled && showOptionsButton && (
        <ColumnOptionsButton
          column={context.column as Column<any, unknown>}
          storedColumnSizing={storedColumnSizing}
          canHide={canHide}
          onResetWidth={() =>
            onColumnSizingChange((prev) => {
              const { [context.column.id]: _, ...rest } = prev;
              return rest;
            })
          }
        />
      )}
    </div>
  );
};

const TooltipContent = ({
  ctx,
  groupingOptions,
}: {
  ctx: HeaderContext<any, any>;
  groupingOptions?: GroupingOption[];
}) => {
  const { context } = useTooltipContext();

  const isSorted = ctx.column.getIsSorted();
  const isSortedDesc = isSorted ? isSorted === 'desc' : undefined;
  const canGroupBy = ctx.column.getCanGroup();
  const isGrouped = ctx.column.getIsGrouped();
  const canSort = ctx.column.getCanSort();
  const groupBy = ctx.table.getState().grouping;

  return (
    <HeaderCellFiltersDropdown
      closeTooltip={() => {
        context.onOpenChange(false);
      }}
      canGroupBy={canGroupBy}
      isGroupedByColumn={isGrouped}
      toggleGroupByColumn={ctx.column.toggleGrouping}
      setGroupBy={ctx.table.setGrouping}
      groupBy={groupBy}
      clearSortBy={ctx.column.clearSorting}
      isSortedDesc={isSortedDesc}
      toggleSortBy={ctx.column.toggleSorting}
      groupingOptions={groupingOptions}
      canSort={canSort}
    />
  );
};

const ChevronIcon = ({ className, color = '#969696' }: IconProps) => (
  <svg
    className={classNames('w-3 h-3', className)}
    width='14'
    height='9'
    viewBox='0 0 14 9'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path d='M1 1L7 7L13 1' stroke={color} strokeWidth='2' />
  </svg>
);
